/**
 * Coach conflict detection utilities for assignment flows.
 *
 * Pure functions that check scheduling conflicts and interest limits
 * using data already present on CohortCoachWithDetails objects.
 *
 * MED-978: scheduling conflicts now filter to the group's actual scheduled
 * session dates — a date-level conflict on the slot only applies to the
 * group(s) whose session falls on that date, mirroring the rule the API
 * already applies in CohortCoachRepository.getSlotDateForSession.
 */

import type { CohortCoachWithDetails, SlotConflict } from '../../../../services/api/cohortCoaches';
import type { GroupWithMembers } from '../../../../services/api/groups';
import type { SessionWithDetails } from '../../../../services/api/sessions';

// ── Types ────────────────────────────────────────────────────────────────

export interface SchedulingConflictWarning {
  type: 'scheduling';
  groupId: string;
  groupName: string;
  conflicts: SlotConflict[];
}

export interface InterestLimitWarning {
  type: 'interest_limit';
  currentCount: number;
  maxGroups: number;
}

export type CoachAssignmentWarning = SchedulingConflictWarning | InterestLimitWarning;

// Map of groupId → that group's sessions. Threaded through the conflict
// helpers so date-level conflicts only apply to the group whose session
// falls on the conflict date.
export type GroupSessionsMap = Record<string, SessionWithDetails[] | undefined>;

// ── Internal date helpers ────────────────────────────────────────────────

const DAY_OF_WEEK_NUM: Record<string, number> = {
  sunday: 0,
  monday: 1,
  tuesday: 2,
  wednesday: 3,
  thursday: 4,
  friday: 5,
  saturday: 6,
};

// Convert a session's scheduled_start (ISO timestamp) to a YYYY-MM-DD date in
// UTC. Matches the API-side derivation so SlotConflict.date comparisons line
// up across the stack.
function getSessionDateUTC(scheduledStart: string | null | undefined): string | null {
  if (!scheduledStart) return null;
  return new Date(scheduledStart).toISOString().split('T')[0];
}

// Direct port of getSlotDateForSession in
// medley-api/repositories/CohortCoachRepository.ts. Given a session's
// scheduled date (YYYY-MM-DD) and the slot's day-of-week, return the
// calendar date the slot falls on in that session's week.
function getSlotDateForSession(
  sessionScheduledDate: string | null,
  slotDayOfWeek: string | undefined | null,
): string | null {
  if (!sessionScheduledDate || !slotDayOfWeek) return null;
  const sessionDate = new Date(sessionScheduledDate + 'T12:00:00Z'); // noon UTC avoids TZ edge cases
  const sessionDay = sessionDate.getUTCDay();
  const slotDay = DAY_OF_WEEK_NUM[slotDayOfWeek.toLowerCase()];
  if (slotDay === undefined) return null;

  const offset = (slotDay - sessionDay + 7) % 7;
  const slotDate = new Date(sessionDate);
  slotDate.setUTCDate(slotDate.getUTCDate() + offset);
  return slotDate.toISOString().split('T')[0];
}

// ── Shared availability helper (used by both surfaces) ───────────────────

/**
 * Return the conflicts in `coach.slot_availability[group.scheduled_time_slot_id]`
 * that actually apply to THIS group — i.e. whose `date` matches one of the
 * group's scheduled slot-dates. The single source of truth for the
 * Coaches-tab and Groups-screen surfaces (MED-978 AC1/AC2/AC3).
 *
 * Rules:
 *  - No slot, no slot_availability entry, or empty conflicts array → [].
 *  - Conflicts filtered to dates matching the group's session slot-dates.
 *  - If the group has no sessions (yet) we can't compute matches; return []
 *    rather than blanket-flagging — the conservative "show clean" fallback.
 *
 * Honors the `status` field implicitly: a `full`/`available` slot only has
 * conflicts when the data backend chose to record them, and we then only
 * apply those that match the group's dates. A `partial` slot works the same
 * way. A truly `unavailable` slot is surfaced through the slot-status flow
 * (confirmed_time_slots / availability_status), not through conflicts.
 */
export function getEffectiveConflictsForGroup(
  coach: CohortCoachWithDetails,
  group: GroupWithMembers,
  groupSessions: SessionWithDetails[] | undefined,
): SlotConflict[] {
  if (!group.scheduled_time_slot_id) return [];
  if (!coach.slot_availability) return [];

  const slotAvail = coach.slot_availability[group.scheduled_time_slot_id];
  if (!slotAvail) return [];

  const rawConflicts = slotAvail.conflicts;
  if (!rawConflicts || rawConflicts.length === 0) return [];

  const slotDayOfWeek = group.time_slot_day;
  if (!slotDayOfWeek) return [];

  const groupSlotDates = new Set<string>();
  if (groupSessions) {
    for (const session of groupSessions) {
      const sessionDate = getSessionDateUTC(session.scheduled_start);
      const slotDate = getSlotDateForSession(sessionDate, slotDayOfWeek);
      if (slotDate) groupSlotDates.add(slotDate);
    }
  }

  if (groupSlotDates.size === 0) return [];

  return rawConflicts.filter((c) => groupSlotDates.has(c.date));
}

// ── Public functions ────────────────────────────────────────────────────

/**
 * Get scheduling conflicts for a coach + group pair.
 *
 * MED-978: requires the group's sessions so conflicts are filtered to the
 * group's actual scheduled dates. A conflict on a date that does not match
 * one of this group's sessions does NOT flag this group.
 */
export function getSchedulingConflicts(
  coach: CohortCoachWithDetails,
  group: GroupWithMembers,
  groupSessions: SessionWithDetails[] | undefined,
): SlotConflict[] {
  return getEffectiveConflictsForGroup(coach, group, groupSessions);
}

/**
 * Check if assigning newGroupCount more groups would exceed the coach's
 * stated interest limit. Returns null if no warning needed.
 *
 * Respects is_flexible_groups — flexible coaches never trigger this warning.
 */
export function getInterestLimitWarning(
  coach: CohortCoachWithDetails,
  newGroupCount: number,
): InterestLimitWarning | null {
  if (coach.max_groups == null || coach.is_flexible_groups) return null;

  const totalAfterAssignment = coach.group_count + newGroupCount;
  if (totalAfterAssignment <= coach.max_groups) return null;

  return {
    type: 'interest_limit',
    currentCount: coach.group_count,
    maxGroups: coach.max_groups,
  };
}

/**
 * Collect all assignment warnings for a coach being assigned to one or more groups.
 * Used by confirm modal (coach-first, group-first) and drag-drop flows.
 *
 * MED-978: takes a `groupSessionsMap` so scheduling conflicts can be filtered
 * to each group's specific session dates. Pass `{}` only when no session data
 * is available (e.g. groups not yet scheduled) — date filtering then falls
 * back to a clean "no conflicts" view rather than a blanket flag.
 */
export function collectAssignmentWarnings(
  coach: CohortCoachWithDetails,
  groups: GroupWithMembers[],
  groupSessionsMap: GroupSessionsMap,
): CoachAssignmentWarning[] {
  const warnings: CoachAssignmentWarning[] = [];

  for (const group of groups) {
    const sessions = groupSessionsMap[group.id];
    const conflicts = getSchedulingConflicts(coach, group, sessions);
    if (conflicts.length > 0) {
      warnings.push({
        type: 'scheduling',
        groupId: group.id,
        groupName: group.name,
        conflicts,
      });
    }
  }

  const interestWarning = getInterestLimitWarning(coach, groups.length);
  if (interestWarning) {
    warnings.push(interestWarning);
  }

  return warnings;
}
